import { useEffect, useCallback, useRef, useContext, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useVisit } from '@zenbusiness/application-commons-visit';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Container from '@zenbusiness/zui/Container';
import SharedLottieAsset from '@zenbusiness/zui/SharedLottieAsset';

import ChatConnectionError from '#client/components/ChatConnectionError';
import DynamicRenderer from '#client/components/DynamicRenderer';
import StandaloneHeader from '#client/components/StandaloneHeader';
import Transition from '#client/components/Transition';
import { STANDALONE_THREAD_STATUS } from '#client/components/utils/constants';
import { CustomFormContext } from '#client/contexts/CustomFormContext';
import { useInstructionRenderer } from '#client/hooks/useInstructionRenderer';
import { useVisitorThreadCreation } from '#client/hooks/useVisitorThreadCreation';

// import PoweredByVelo from '../components/PoweredByVelo/PoweredByVelo';

const LOADER_ICON = (
  <SharedLottieAsset height="20rem" src="/s2/animation/lottie/velo-avatar-loading-light.json" />
);

/**
 * Convert user selections from ChoiceFlow to a string for the agent
 * @param {object} obj - User selections object
 * @returns {string} Formatted string of selections
 */
const userSelectionToString = (obj) => {
  return Object.entries(obj)
    .map(([key, value]) => {
      if (value instanceof Object) {
        if (value.selectedId === 'other') {
          return `${key}: ${value.inputValue}`;
        }
      }
      return `${key}: ${value?.value || value}`;
    })
    .join('. ');
};

/**
 * Helper functions for thread state management
 * @param {string} persistKey - Key for localStorage persistence
 * @returns {object|null} Stored thread state or null
 */
const getStoredThreadState = (persistKey) => {
  try {
    const stored = localStorage.getItem(persistKey);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn(`[StandaloneVisitorPage] Failed to parse thread state from localStorage:`, error);
    return null;
  }
};

const setStoredThreadState = (persistKey, threadUuid, status) => {
  try {
    localStorage.setItem(persistKey, JSON.stringify({ threadUuid, status }));
  } catch (error) {
    console.warn(`[StandaloneVisitorPage] Failed to store thread state in localStorage:`, error);
  }
};

const clearStoredThreadState = (persistKey) => {
  try {
    localStorage.removeItem(persistKey);
  } catch (error) {
    console.warn(`[StandaloneVisitorPage] Failed to clear thread state from localStorage:`, error);
  }
};

const clearAllRelatedStorage = (persistKey) => {
  clearStoredThreadState(persistKey);
  localStorage.removeItem(`${persistKey}_form`);
  localStorage.removeItem(`${persistKey}_flow`);
};

/**
 * StandaloneVisitorPage - A reusable visitor page component for standalone tools
 *
 * This component handles:
 * - Tag-based feature flag filtering
 * - Thread creation and management
 * - UI instruction rendering
 * - Form submission handling
 * - State persistence
 *
 * @param {object} props - Component props
 * @param {string} props.pageTag - Unique identifier for this page (used for feature flag filtering)
 * @param {string} props.formAgentName - Agent name for the form (e.g., 'market-analysis-form')
 * @param {string} props.reportAgentName - Agent name for the report (e.g., 'market-analysis')
 * @param {string} props.persistKey - LocalStorage key for state persistence
 * @param {string} [props.flowKey] - URL segment key (defaults to persistKey)
 * @param {string} [props.title] - Header title for the page
 * @param {string} [props.subtitle] - Header subtitle for the page
 * @param {object} [props.transitions] - Custom transition messages
 * @param {string} [props.generateMorePrompt] - Custom prompt prefix for generate more action (default: 'Generate more based on these options: ')
 * @returns {object} The rendered StandaloneVisitorPage component
 */
export default function StandaloneVisitorPage({
  pageTag,
  formAgentName,
  reportAgentName,
  persistKey,
  flowKey = null,
  title = null,
  subtitle = null,
  transitions = {},
  generateMorePrompt = 'Generate more based on these options: '
}) {
  const { visitUuid, visitProcessed } = useVisit();
  const navigate = useNavigate();

  // Fetch excluded pages from feature flag
  // Default to empty array (all pages enabled)
  // Use withLoadingFlag: true to properly handle async flag loading
  const flagResult = useExperimentFlag(
    'configure-velo-exclude-standalone-pages',
    [], // Default: empty array (all pages enabled)
    true // Show loading state to ensure flag is loaded
  );

  const flagLoading = flagResult?.loading ?? false;

  // Ensure excludedPages is always an array (handle edge cases)
  const excludedPagesArray = useMemo(() => {
    const excludedPages = flagResult?.value ?? [];
    if (!Array.isArray(excludedPages)) {
      console.warn(
        '[StandaloneVisitorPage] excludedPages is not an array:',
        excludedPages,
        typeof excludedPages
      );
      // Try to parse if it's a string
      if (typeof excludedPages === 'string') {
        try {
          const parsed = JSON.parse(excludedPages);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.warn('[StandaloneVisitorPage] Failed to parse excludedPages as JSON:', e);
          return [];
        }
      }
      return [];
    }
    return excludedPages;
  }, [flagResult?.value]);

  // Check if this page is excluded
  const isPageExcluded = useMemo(() => {
    if (!pageTag) {
      console.warn('[StandaloneVisitorPage] pageTag is required for feature flag filtering');
      return false;
    }
    return excludedPagesArray.includes(pageTag);
  }, [excludedPagesArray, pageTag]);

  // Page is enabled if not in excluded pages list and flag has loaded
  const featureEnabled = !isPageExcluded && !flagLoading;
  // Redirect if page is disabled
  useEffect(() => {
    if (!featureEnabled) {
      navigate('/', { replace: true });
    }
  }, [featureEnabled, navigate]);

  // Thread creation hook
  const {
    loading: threadLoading,
    error: threadError,
    customError: threadCustomError,
    createNewThread,
    retryThreadCreation,
    canRetry,
    retryCount,
    clearError
  } = useVisitorThreadCreation();

  // Instruction renderer hook
  const {
    error: instructionError,
    instructions,
    isError: isInstructionError,
    loadingNext,
    loadNextInstruction,
    loadExistingMessages,
    resetStateForNewThread,
    setInstructions
  } = useInstructionRenderer();

  // Get form answers from CustomFormContext as fallback (safely handle if provider not available)
  const formContext = useContext(CustomFormContext);
  const formAnswers = useMemo(() => formContext?.answers || {}, [formContext?.answers]);

  // Create thread callback
  const createThread = useCallback(
    async (visitUuidParam) => {
      // Check for existing in-progress thread
      const storedThreadState = getStoredThreadState(persistKey);
      if (
        storedThreadState &&
        storedThreadState.status === STANDALONE_THREAD_STATUS.in_progress &&
        storedThreadState.threadUuid
      ) {
        // Resume existing thread
        resetStateForNewThread(storedThreadState.threadUuid);
        loadExistingMessages(
          { visitUuid: visitUuidParam },
          storedThreadState.threadUuid,
          'standalone'
        );
        return storedThreadState.threadUuid;
      }

      // Create new thread with form agent
      const threadInput = {
        visitUuid: visitUuidParam,
        agentName: formAgentName,
        message: `Start ${flowKey || persistKey} flow`,
        messageType: 'invoke',
        role: 'user',
        visible: false
      };

      const { threadUuid: newThreadUuid, data } = await createNewThread(
        visitUuidParam,
        threadInput,
        { redirect: false }
      );

      // Store thread state
      resetStateForNewThread(newThreadUuid);
      setStoredThreadState(persistKey, newThreadUuid, STANDALONE_THREAD_STATUS.in_progress);
      setInstructions(data);
      return newThreadUuid;
    },
    [
      createNewThread,
      formAgentName,
      flowKey,
      loadExistingMessages,
      persistKey,
      resetStateForNewThread,
      setInstructions
    ]
  );

  // Track thread creation to prevent double execution
  const threadCreationInitiated = useRef(false);

  // Reset flag when visitUuid changes
  useEffect(() => {
    threadCreationInitiated.current = false;
  }, [visitUuid]);

  // Clear stale thread state on mount
  useEffect(() => {
    const storedThreadState = getStoredThreadState(persistKey);
    if (
      storedThreadState &&
      storedThreadState.status !== STANDALONE_THREAD_STATUS.in_progress &&
      !threadCreationInitiated.current
    ) {
      clearAllRelatedStorage(persistKey);
    }
  }, [persistKey]);

  // Auto-create thread on mount
  useEffect(() => {
    // Wait for flag to load before checking featureEnabled
    if (flagLoading || !featureEnabled) {
      return;
    }

    // Handle invalid thread (e.g., deleted by cronjob)
    const threadNotFound = isInstructionError && instructionError?.errorType === 'INVALID_THREAD';
    if (threadNotFound) {
      threadCreationInitiated.current = false;
      clearAllRelatedStorage(persistKey);
    }

    // Create thread if conditions are met
    // Stop retrying if there's an error (either threadError or customError)
    const hasError = threadError || threadCustomError;
    if (!threadLoading && !hasError && visitUuid && !threadCreationInitiated.current) {
      createThread(visitUuid)
        .then((newThreadUuid) => {
          if (newThreadUuid) {
            threadCreationInitiated.current = true;
          }
        })
        .catch((error) => {
          // On error, set flag to prevent infinite retries
          console.error('[StandaloneVisitorPage] Thread creation failed:', error);
          threadCreationInitiated.current = true;
        });
    }
  }, [
    createThread,
    featureEnabled,
    flagLoading,
    instructionError?.errorType,
    isInstructionError,
    persistKey,
    threadError,
    threadCustomError,
    threadLoading,
    visitUuid
  ]);

  // Handle retry for thread creation
  const handleRetryThreadCreation = useCallback(() => {
    // Reset the flag to allow retry
    threadCreationInitiated.current = false;
    // Clear error state
    clearError();
    // Retry thread creation
    if (visitUuid) {
      const threadInput = {
        visitUuid,
        agentName: formAgentName,
        message: `Start ${flowKey || persistKey} flow`,
        messageType: 'invoke',
        role: 'user',
        visible: false
      };
      retryThreadCreation(visitUuid, threadInput);
    }
  }, [clearError, flowKey, formAgentName, persistKey, retryThreadCreation, visitUuid]);

  // Handle start new (refresh page)
  const handleStartNew = useCallback(() => {
    clearAllRelatedStorage(persistKey);
    navigate(0); // Refresh page
  }, [navigate, persistKey]);

  // Handle component click events
  const handleComponentClick = useCallback(
    async (action) => {
      // Get user answers from action payload or fallback to form context
      const userProfile = action?.payload?.answers || formAnswers || {};

      // Form submission - call report agent
      if (action.type === 'choice_flow_submission') {
        await loadNextInstruction(userSelectionToString(userProfile), 'invoke', 'standalone', {
          visitUuid,
          agentName: reportAgentName
        });
        const storedThreadState = getStoredThreadState(persistKey);
        if (storedThreadState?.threadUuid) {
          setStoredThreadState(
            persistKey,
            storedThreadState.threadUuid,
            STANDALONE_THREAD_STATUS.complete
          );
        }
      }

      // Generate more action
      if (action.type === 'generate_more' || action.type === 'business_results_generate_more') {
        const userAnswersString = userSelectionToString(userProfile);
        const prompt = userAnswersString
          ? `${generateMorePrompt}${userAnswersString}`
          : generateMorePrompt.trim();
        await loadNextInstruction(prompt, 'invoke', 'standalone', {
          visitUuid,
          agentName: reportAgentName
        });
      }

      // Start over action
      if (action.type === 'start_over' || action.type === 'business_results_start_over') {
        clearAllRelatedStorage(persistKey);
        navigate(0); // Refresh page
      }

      // Download actions are handled by the report component itself (e.g., BusinessResults.handleDownloadReport)
      // No need to handle them here as each report component knows how to generate its own PDF
    },
    [
      formAnswers,
      generateMorePrompt,
      loadNextInstruction,
      navigate,
      persistKey,
      reportAgentName,
      visitUuid
    ]
  );

  // Render loading states
  if (!visitProcessed) {
    return <Transition icon={LOADER_ICON} type={transitions.loading || 'VISIT_LOADING'} />;
  }

  // Show loading while feature flag is being evaluated
  if (flagLoading) {
    return <Transition icon={LOADER_ICON} type={transitions.loading || 'LOADING'} />;
  }

  // Show error state with retry options
  const hasThreadError = threadError || threadCustomError;
  if (hasThreadError && !threadLoading) {
    // If we have instructions, use ChatConnectionError (for connection errors after thread exists)
    if (instructions.length > 0) {
      return (
        <ChatConnectionError
          instructions={instructions}
          type="CONNECTION_ERROR"
          onRetry={canRetry ? handleRetryThreadCreation : () => {}}
          onStartNew={handleStartNew}
        />
      );
    }

    // For thread creation errors (no instructions yet), show custom error UI
    const getErrorType = () => {
      const error = threadError || threadCustomError;
      if (error?.message?.includes('401') || error?.message?.includes('authentication')) {
        return 'THREAD_ERROR_AUTH';
      }
      if (
        error?.message?.includes('fetch') ||
        error?.message?.includes('Network') ||
        error?.networkError
      ) {
        return 'THREAD_ERROR_NETWORK';
      }
      return 'THREAD_ERROR_GENERIC';
    };

    const getRetryButtonText = () => {
      if (retryCount > 0) {
        return `Retry (${retryCount}/3)`;
      }
      return 'Try Again';
    };

    return (
      <Transition
        actions={
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {canRetry && (
              <Button color="primary" variant="contained" onClick={handleRetryThreadCreation}>
                {getRetryButtonText()}
              </Button>
            )}
            <Button color="primary" variant="outlined" onClick={handleStartNew}>
              Start New Conversation
            </Button>
          </Box>
        }
        type={getErrorType()}
      />
    );
  }

  if (threadLoading) {
    return (
      <Transition
        icon={LOADER_ICON}
        type={
          retryCount > 0
            ? transitions.loading || 'THREAD_CREATING_RETRY'
            : transitions.loading || 'THREAD_CREATING'
        }
        typeProps={{ retryCount }}
      />
    );
  }

  if (loadingNext && threadCreationInitiated.current) {
    return <Transition icon={LOADER_ICON} type={transitions.generating || 'GENERATING_RESULTS'} />;
  }

  if (loadingNext && !threadCreationInitiated.current) {
    return <Transition icon={LOADER_ICON} type={transitions.loading || 'LOADING'} />;
  }

  // Render the dynamic UI
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header - full width */}
      <StandaloneHeader subtitle={subtitle} title={title} />
      {/* Main content */}
      <Container>
        <DynamicRenderer
          currentMode="initial"
          fullWidth
          instructions={instructions}
          isChatMode={false}
          onComponentClick={handleComponentClick}
        />
      </Container>
      {/* Footer */} {/* TODO: Uncomment this when ready change all the pages to use this */}
      {/* <Container sx={{ maxWidth: '67rem' }}>
        <PoweredByVelo />
      </Container> */}
    </Box>
  );
}

StandaloneVisitorPage.propTypes = {
  pageTag: PropTypes.string.isRequired,
  formAgentName: PropTypes.string.isRequired,
  reportAgentName: PropTypes.string.isRequired,
  persistKey: PropTypes.string.isRequired,
  flowKey: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  transitions: PropTypes.shape({
    generating: PropTypes.string,
    loading: PropTypes.string
  }),
  generateMorePrompt: PropTypes.string
};
